// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import { CROP_STATUS, cropStepNameToShortName, isDurationStep } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router';
import { PageError } from '../components/common/error';
import { Time } from '../components/common/time';
import PageView from '../components/common/view/PageView';
import CropOptions from '../components/crop/CropOptions';
import CropStepsView from '../components/crop/CropStepsView';
import { CropTimelineCard } from '../components/crop/CropTimelineCalculator';
import GrowthTimeline from '../components/crop/GrowthTimeline';
import { addDurations, sortCropSteps } from '../components/crop/utils';
import { SizesView } from '../components/size';
import { useUserContext } from '../context/UserContext';
import { Box } from '../lib/base';
import { SINGLE_CROP_QUERY } from '../queries/SingleCropQuery';
import * as routes from '../routes';
import type { SingleCropQuery, SingleCropQueryVariables } from '../types/__generated__/graphql';
import { createID, formatDuration, formatStateOrStatus, formatWeightUnits, getNumericID } from '../utils';

export default function CropView() {
  const { cropId } = useParams<{ readonly cropId: string }>();
  const { permissions } = useUserContext();
  if (cropId === undefined) {
    return <PageError />;
  }
  const globalCropId = createID.crop(cropId);
  const { data, loading, error, refetch } = useQuery<SingleCropQuery, SingleCropQueryVariables>(SINGLE_CROP_QUERY, {
    variables: { id: globalCropId },
  });
  if (error) {
    return <PageError error={error} />;
  }
  if (loading || data?.crop == null) {
    return (
      <PageView
        title="Crop"
        name="Crop"
        pluralName="Crops"
        pluralRoute={routes.CROPS}
        loading={loading}
        searchRoute={''}
        bodyColumns={[]}
      />
    );
  }

  const userCanEditCrops = permissions.can_edit_crops_blends;
  const sizes = data.crop.edge_sizes.edges.map(({ node }) => ({
    ...node,
    id: getNumericID(node.id),
    size: Number.parseFloat(node.size),
  }));
  const sizesView = (
    <SizesView
      sizes={sizes}
      loading={loading}
      onSuccess={(shouldRefetch) => {
        if (shouldRefetch) {
          void refetch();
        }
      }}
    />
  );

  const steps = data.crop.steps.edges.map(({ node }) => node);
  steps.sort(sortCropSteps);
  const totalGrowTime = data.crop.total_grow_time;
  const stepsWithDurations = addDurations(steps, totalGrowTime);

  const stepsView = <CropStepsView sortedSteps={stepsWithDurations} loading={loading} />;

  const durationSteps = stepsWithDurations.filter((s) => isDurationStep(s.name));
  const phases = durationSteps.map((step) => {
    return { label: cropStepNameToShortName(step.name), duration: step.duration, id: step.id };
  });
  const growthTimeline =
    phases.length > 0 ? (
      <Box sx={{ mt: 2 }}>
        <GrowthTimeline phases={phases} />
      </Box>
    ) : null;

  const timelineCalculator = (
    <Box sx={{ mt: 2 }}>
      <CropTimelineCard totalGrowTime={totalGrowTime} bufferTime={data.crop.harvest_buffer_time || 0} steps={steps} />
    </Box>
  );

  const extra = (
    <>
      {sizesView}
      {timelineCalculator}
      {growthTimeline}
      {stepsView}
    </>
  );

  const columns: Array<[string, React.ReactElement | string] | [string, React.ReactElement | string, number]> = [
    ['Crop Name', data.crop.crop_name],
    ['Crop ID', getNumericID(data.crop.id).toString()],
    ['Total Grow Time', formatDuration(totalGrowTime)],
    ['Expected Yield', `${data.crop.yield_per_tray} ${data.crop.yield_unit} per tray`],
    ['Shelf Life', data.crop.max_hold_time ? formatDuration(data.crop.max_hold_time) : '-'],
    ['Harvest Buffer Time', data.crop.harvest_buffer_time ? formatDuration(data.crop.harvest_buffer_time) : '-'],
    [
      'Sow Density',
      data.crop.sowing_density
        ? `${formatWeightUnits(Number.parseFloat(data.crop.sowing_density), data.crop.sow_density_unit)} per tray`
        : '-',
    ],
    ['Safety Margin', `${data.crop.buffer_percentage.toString()}%`],
    ['Status', formatStateOrStatus(data.crop.status)],
    ['Notes', data.crop.notes || '-'],
    [
      'Updated At',
      <Time key={1} time={data.crop.updated_at} options={{ time: true, removeDayOfWeek: true }} dontUseTimezone />,
    ],
    [
      'Created At',
      <Time key={1} time={data.crop.created_at} options={{ time: true, removeDayOfWeek: true }} dontUseTimezone />,
    ],
  ];

  return (
    <>
      <Helmet>
        <title>{data.crop.crop_name}</title>
      </Helmet>
      <PageView
        title="Crop"
        name="Crop"
        pluralName="Crops"
        pluralRoute={routes.CROPS}
        searchRoute={routes.makeCropSearch(getNumericID(data.crop.id))}
        bodyColumns={columns}
        extra={extra}
        loading={loading}
        headerRightElement={
          userCanEditCrops ? (
            <CropOptions
              cropName={data.crop.crop_name}
              isArchived={data.crop.status === CROP_STATUS.ARCHIVED}
              cropId={globalCropId}
              onSuccess={(shouldRefetch) => {
                if (shouldRefetch) {
                  void refetch();
                }
              }}
            />
          ) : (
            <></>
          )
        }
      />
    </>
  );
}
